import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { kandidaatSleutels } from './kandidaten.ts';
import { useApiMutatie } from './mutaties.ts';
import type {
  AfrondingInvoer,
  FaseInvoer,
  LopendeFase,
  Paginering,
  Voordracht,
  VoordrachtDetails,
  VoordrachtInvoer,
} from './typen.ts';
import { vacatureSleutels } from './vacatures.ts';

export const voordrachtSleutels = {
  alle: ['voordrachten'] as const,
  lopend: (fase: LopendeFase | undefined, paginering: Paginering) =>
    ['voordrachten', 'lopend', fase ?? 'alle', paginering] as const,
  details: (id: string) => ['voordrachten', id] as const,
  vanKandidaat: (kandidaatId: string) => ['voordrachten', 'kandidaat', kandidaatId] as const,
  vanVacature: (vacatureId: string) => ['voordrachten', 'vacature', vacatureId] as const,
};

/**
 * Somt de lopende voordrachten op, optioneel in één fase.
 * @param fase De fase, of `undefined` voor alle lopende voordrachten.
 * @param paginering Limiet en offset.
 * @returns De query met de voordrachten.
 */
export function useLopendeVoordrachten(fase: LopendeFase | undefined, paginering: Paginering = { limiet: 500 }) {
  return useQuery({
    queryKey: voordrachtSleutels.lopend(fase, paginering),
    queryFn: () =>
      uitvoeren(api.GET('/api/v1/voordrachten', { params: { query: fase ? { fase, ...paginering } : paginering } })),
    placeholderData: keepPreviousData,
  });
}

function haalDetails(id: string): Promise<VoordrachtDetails> {
  return uitvoeren(api.GET('/api/v1/voordrachten/{id}', { params: { path: { id } } }));
}

/**
 * Haalt een voordracht op met kandidaat, vacature, cv en geschiedenis.
 * @param id Het id van de voordracht.
 * @returns De query met de voordracht.
 */
export function useVoordracht(id: string | undefined) {
  return useQuery({
    queryKey: voordrachtSleutels.details(id ?? ''),
    queryFn: () => haalDetails(id ?? ''),
    enabled: Boolean(id),
  });
}

/**
 * Haalt voor een lijst voordrachten de details met kandidaat en vacature op. De lijstroutes geven alleen id's;
 * de details worden per voordracht opgehaald en gedeeld via de cache.
 * @param voordrachten De voordrachten uit een lijst.
 * @returns De details per voordracht, in dezelfde volgorde, met `undefined` zolang ze laden.
 */
export function useVoordrachtDetails(voordrachten: readonly Voordracht[] | undefined) {
  return useQueries({
    queries: (voordrachten ?? []).map((voordracht) => ({
      queryKey: voordrachtSleutels.details(voordracht.id),
      queryFn: () => haalDetails(voordracht.id),
      staleTime: 30_000,
    })),
    combine: (resultaten) => ({
      details: resultaten.map((resultaat) => resultaat.data),
      laden: resultaten.some((resultaat) => resultaat.isPending),
    }),
  });
}

/**
 * Somt de voordrachten van een kandidaat op.
 * @param kandidaatId Het id van de kandidaat.
 * @returns De query met de voordrachten.
 */
export function useVoordrachtenVanKandidaat(kandidaatId: string | undefined) {
  return useQuery({
    queryKey: voordrachtSleutels.vanKandidaat(kandidaatId ?? ''),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/kandidaten/{id}/voordrachten', {
          params: { path: { id: kandidaatId ?? '' }, query: { limiet: 100 } },
        }),
      ),
    enabled: Boolean(kandidaatId),
  });
}

/**
 * Somt de voordrachten op een vacature op.
 * @param vacatureId Het id van de vacature.
 * @returns De query met de voordrachten.
 */
export function useVoordrachtenVanVacature(vacatureId: string | undefined) {
  return useQuery({
    queryKey: voordrachtSleutels.vanVacature(vacatureId ?? ''),
    queryFn: () =>
      uitvoeren(
        api.GET('/api/v1/vacatures/{id}/voordrachten', {
          params: { path: { id: vacatureId ?? '' }, query: { limiet: 100 } },
        }),
      ),
    enabled: Boolean(vacatureId),
  });
}

/**
 * De mutaties op voordrachten: voordragen, naar een fase, afronden en heropenen.
 * @returns De mutaties.
 */
export function useVoordrachtMutaties() {
  const verversen = (_invoer: unknown, voordracht: Voordracht) => [
    voordrachtSleutels.alle,
    kandidaatSleutels.profiel(voordracht.kandidaatId),
    vacatureSleutels.details(voordracht.vacatureId),
  ];
  return {
    draagVoor: useApiMutatie(
      (invoer: VoordrachtInvoer) => uitvoeren(api.POST('/api/v1/voordrachten', { body: invoer })),
      verversen,
    ),
    naarFase: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: FaseInvoer }) =>
        uitvoeren(api.POST('/api/v1/voordrachten/{id}/fase', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    rondAf: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: AfrondingInvoer }) =>
        uitvoeren(api.POST('/api/v1/voordrachten/{id}/afronding', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
    heropen: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: FaseInvoer }) =>
        uitvoeren(api.POST('/api/v1/voordrachten/{id}/heropening', { params: { path: { id } }, body: invoer })),
      verversen,
    ),
  };
}
